import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  forwardRef,
  TemplateRef,
  Injector,
  inject,
  output,
  input,
  contentChild,
  contentChildren,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  DropDownFilterSettings,
  DropDownListComponent,
  DropDownListModule,
  PopupSettings,
} from '@progress/kendo-angular-dropdowns';
import { FloatingLabelComponent } from '@progress/kendo-angular-label';
import { FooterTemplateDirective } from '../../directives/footer-template.directive';
import { ListGroupTemplateDirective } from '../../directives/list-group-template.directive';
import { ValueTemplateDirective } from '../../directives/value-template.directive';
import { DropdownsItemTemplateDirective } from '../../directives/dropdowns-item-template.directive';
import { FixedGroupTemplateDirective } from '../../directives/fixed-group-template.directive';
import { GroupTemplateDirective } from '../../directives/group-template.directive';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'assist-dropdownlist',
  templateUrl: './dropdownlist.component.html',
  styleUrls: ['./dropdownlist.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DropDownListModule, ReactiveFormsModule, NgTemplateOutlet],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownlistComponent),
      multi: true,
    },
  ],
})
export class DropdownlistComponent implements OnInit, ControlValueAccessor {
  public floatingLabel = inject(FloatingLabelComponent, { optional: true });
  private controlContainer = inject(ControlContainer, { optional: true, host: true, skipSelf: true });
  private injector = inject(Injector);
  kendoDropdown = viewChild(DropDownListComponent);
  valueTemplate = contentChild(ValueTemplateDirective, { read: TemplateRef });
  itemTemplate = contentChild(DropdownsItemTemplateDirective, { read: TemplateRef });
  fixedGroupTemplate = contentChildren(FixedGroupTemplateDirective, { read: TemplateRef });
  listGroupTemplate = contentChild(ListGroupTemplateDirective, { read: TemplateRef });
  footerTemplate = contentChild(FooterTemplateDirective, { read: TemplateRef });
  groupTemplate = contentChild(GroupTemplateDirective, { read: TemplateRef });
  filterable = input<boolean>();
  popupSettings = input<PopupSettings>();
  data = input();
  placeholder = input<string>();
  dropdownFilter = input<DropDownFilterSettings>();
  textField = input<string>();
  valueField = input<string>();
  allowCustom = input<boolean>();
  listHeight = input<number>(200);
  adaptiveMode = input<'auto' | 'none'>('none');
  valueNormalizer = input<(any) => any>((x) => x);
  value = input<any>();
  valuePrimitive = input<boolean>();
  valueChange = output<any[]>();
  filterChange = output<string>();
  closed = output<any>();

  _control: AbstractControl;

  onFilterFocus() {
    this.kendoDropdown().onFilterFocus();
  }

  toggle(open: boolean) {
    this.kendoDropdown().toggle(open);
  }

  writeValue(obj: any): void {}

  registerOnChange(fn: any): void {}

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}

  ngOnInit(): void {
    const ngControl: NgControl = this.injector.get(NgControl, null);

    if (ngControl == null) {
      this._control = new FormControl<any>(this.value());
      return;
    }
    if (ngControl.control) {
      this._control = ngControl.control as FormControl<any>;
    } else {
      if (this.controlContainer) {
        this._control = (this.controlContainer.control as any).get(ngControl.name);
      } else {
        throw Error('No control found!');
      }
    }
  }

  focus() {
    if (this.floatingLabel) {
      this.floatingLabel.focused = true;
    }
    this.kendoDropdown()?.wrapper?.nativeElement?.classList.add('k-focus');
  }

  blur() {
    if (this.floatingLabel) {
      this.floatingLabel.focused = false;
    }
    this.kendoDropdown()?.wrapper?.nativeElement?.classList.remove('k-focus');
  }
}
